import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { X } from 'lucide-react';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { cn } from '@/lib/utils';

import { MATERIAL_OPTIONS } from '../../constants/style-settings';
import { getColorFromName } from '../../utils/getColorFromName';
import { StyleSettings } from './style-settings';

interface MaterialSettingsProps {
  materials?: MATERIAL_OPTIONS[];
  onUpdate: (key: keyof StyleSettings, val: MATERIAL_OPTIONS) => void;
}

const MaterialItem = ({
  active,
  material,
  label,
  onClickItem,
}: {
  active: boolean;
  material: MATERIAL_OPTIONS;
  label: string;
  onClickItem: (key: keyof StyleSettings, val: MATERIAL_OPTIONS) => void;
}) => {
  return (
    <div
      className={cn(
        'flex cursor-pointer items-center gap-1 rounded-sm px-2 py-1',
        active ? 'bg-[#07B1BE1F]' : 'bg-[#0000001F]'
      )}
      onClick={() => onClickItem('materials', material)}
    >
      <div
        className="h-[13px] w-[13px] rounded-full"
        style={{ backgroundColor: getColorFromName(material) }}
      />
      <span className="text-[10px] font-medium leading-none">{label}</span>
      {!active ? <X className="h-[10px] w-[10px]" /> : null}
    </div>
  );
};

export const MaterialSettings = ({ materials, onUpdate }: MaterialSettingsProps) => {
  const { t } = useTranslation();
  const [isOpened, setIsOpened] = useState(false);

  const materialOptions = [
    MATERIAL_OPTIONS.WOOD,
    MATERIAL_OPTIONS.LEATHER,
    MATERIAL_OPTIONS.COPPER,
    MATERIAL_OPTIONS.STEEL,
    MATERIAL_OPTIONS.GLASS,
    MATERIAL_OPTIONS.MARBLE,
    MATERIAL_OPTIONS.GRANITE,
    MATERIAL_OPTIONS.CONCRETE,
    MATERIAL_OPTIONS.BRICK,
    MATERIAL_OPTIONS.CERAMIC,
    MATERIAL_OPTIONS.PORCELAIN,
    MATERIAL_OPTIONS.BAMBOO,
    MATERIAL_OPTIONS.VELVET,
    MATERIAL_OPTIONS.COTTON,
    MATERIAL_OPTIONS.RATTAN,
  ];

  const materialSets = new Set(materials);
  const selectedMaterials = new Set(
    materialOptions.filter((material) => materialSets.has(material))
  );

  return (
    <Accordion
      type="single"
      className="w-full overflow-hidden rounded-sm bg-[#EFF4F5]"
      onValueChange={(val: string) => setIsOpened(!!val)}
      collapsible
    >
      <AccordionItem value="material-settings">
        <AccordionTrigger className="flex min-h-[40px] w-full cursor-pointer items-center rounded-b-sm px-4 py-2 sm:min-h-[26px] sm:px-2 sm:py-1 lg:min-h-[40px] lg:px-4 lg:py-2">
          <span className="flex-1 text-left text-xs font-semibold lg:text-sm">
            {t('materials')}
          </span>
        </AccordionTrigger>
        {!isOpened && !!selectedMaterials?.size && (
          <div className="flex flex-wrap gap-1 px-4 pb-3">
            {[...selectedMaterials].map((material) => (
              <MaterialItem
                key={material}
                active={materialSets.has(material)}
                material={material}
                label={t(material)}
                onClickItem={onUpdate}
              />
            ))}
          </div>
        )}
        <AccordionContent className="px-4 pb-3">
          <div className="flex flex-wrap gap-1">
            {materialOptions.map((material) => (
              <MaterialItem
                key={material}
                active={materialSets.has(material)}
                material={material}
                label={t(material)}
                onClickItem={onUpdate}
              />
            ))}
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
